import { Check, Copy } from "lucide-react"
import type * as React from "react"
import { useTranslation } from "react-i18next"
import { toast } from "sonner"
import { Button } from "@/components/ui/button"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip"
import { useCopy } from "@/hooks/use-copy"
import { cn } from "@/lib/utils"

/** Icon button (or labelled button) that copies `value` and confirms with a check mark. */
export function CopyButton({
  value,
  label,
  size = "icon-sm",
  variant = "ghost",
  className,
  showLabel = false,
}: {
  value: string
  /** Accessible label / tooltip, defaults to "Copy". */
  label?: string
  size?: React.ComponentProps<typeof Button>["size"]
  variant?: React.ComponentProps<typeof Button>["variant"]
  className?: string
  showLabel?: boolean
}) {
  const { t } = useTranslation()
  const { copied, copy } = useCopy()
  const text = copied ? t("common.copied") : (label ?? t("common.copy"))
  const onClick = async () => {
    const ok = await copy(value)
    if (!ok) toast.error(t("errors.copyFailed"))
  }
  const icon = copied ? <Check className="text-success" /> : <Copy />
  if (showLabel)
    return (
      <Button
        type="button"
        size={size === "icon-sm" ? "sm" : size}
        variant={variant}
        className={className}
        onClick={onClick}
      >
        {icon}
        {text}
      </Button>
    )
  return (
    <Tooltip>
      <TooltipTrigger
        render={
          <Button
            type="button"
            size={size}
            variant={variant}
            className={cn("text-muted-foreground hover:text-foreground", className)}
            onClick={onClick}
            aria-label={text}
          />
        }
      >
        {icon}
      </TooltipTrigger>
      <TooltipContent>{text}</TooltipContent>
    </Tooltip>
  )
}
